import { useOrg } from '../../context/OrgContext';
/**
 * LiveTryonStage — Ray-Ban/Meta-style live virtual try-on.
 *
 * Two modes:
 *  - Live mode (default): clean camera view with the glasses on the face, a
 *    right-side frame grid, brand/name header and Add to Cart. No oval.
 *  - Refine-fit mode: shows the face-alignment oval + progressive guidance
 *    ("Position your face" → "Move closer" → "Almost there" → done) so the user
 *    can check distance/alignment. Toggled by the "Refine fit" button.
 *
 * Fully client-side — MediaPipe (self-hosted WASM + model) + three.js. Arrow keys
 * switch frames. No per-frame backend call.
 */

import { PDMeasurementLib } from '../../lib/tryon/PDMeasurementLib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Product } from '../../types';
import { useCart } from '../../context/CartContext';
import { useLivePricing } from '../../lib/useLivePricing';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';
import { detectRaw, preloadEngine } from '../../lib/tryon/overlayEngine';
import { createAREngine, type AREngine } from '../../lib/tryon/arEngine';
import { resolveFrameUrl, proxyFrameUrl, similarFrames, isGoodFitForShape } from '../../lib/tryon/frameUtils';
import { detectFaceShape, shapeToRecommendedFrames, FACE_SHAPE_GUIDANCE, FaceShapeSmoother, type FaceShape } from '../../lib/tryon/faceShape';
import { FRAME_SHAPES } from '../../types';

interface LiveTryonStageProps {
  products: Product[];
  loadingProducts: boolean;
  /** When set, the grid shows [target + similar] and the target auto-applies. */
  targetProduct?: Product | null;
  compact?: boolean;
  onBrowseAll?: () => void;
  /** Called when the user taps "View Details" on the selected frame's summary. */
  onViewDetails?: (product: Product) => void;
  /**
   * Standalone Try-On Studio presentation (the /tryon-studio route):
   * stationary/softer alignment guide that auto-hides once aligned, a toggleable
   * refine-fit button with the tool bar always visible, no purchase CTA row, and
   * a fullscreen frame carousel. Off = the in-site /tryon page behaviour.
   */
  studio?: boolean;
}

type FitStage = 'searching' | 'far' | 'almost' | 'ok';

// Overlay SVG viewBox. The video is shown mirrored (selfie), and the SVG uses
// preserveAspectRatio="xMidYMid slice" — same fit as the canvas — so landmark
// (x,y) in 0..1 map into the viewBox after mirroring x.
const VB_W = 640, VB_H = 360;
// Fixed fallback ellipse (used before a face is found) — matches the old oval.

// MediaPipe face-oval boundary indices (used to always trace the full outline in
// the detection mesh so the face edges aren't missed by downsampling).
const FACE_OVAL_IDX = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379,
  378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109,
];

/**
 * Build the mirrored "slice" mapping from normalized landmark space (0..1) into
 * the 640×360 overlay viewBox — same fit as the canvas/SVG so points land on the
 * face. Returns per-point map fns, or null if the video isn't sized yet.
 */
// Simple outline glyphs for the detected face shape, shown beside its name.
const FACE_SHAPE_SVGS: Record<FaceShape, React.ReactNode> = {
  Oval: (
    <svg width="32" height="42" viewBox="0 0 36 48" fill="none" stroke="currentColor" strokeWidth="2.5" className="tryon2__faceshape-icon">
      <ellipse cx="18" cy="24" rx="14" ry="20" />
    </svg>
  ),
  Round: (
    <svg width="32" height="42" viewBox="0 0 36 48" fill="none" stroke="currentColor" strokeWidth="2.5" className="tryon2__faceshape-icon">
      <circle cx="18" cy="24" r="16" />
    </svg>
  ),
  Square: (
    <svg width="32" height="42" viewBox="0 0 36 48" fill="none" stroke="currentColor" strokeWidth="2.5" className="tryon2__faceshape-icon">
      <rect x="4" y="8" width="28" height="32" rx="4" />
    </svg>
  ),
  Heart: (
    <svg width="32" height="42" viewBox="0 0 36 48" fill="none" stroke="currentColor" strokeWidth="2.5" className="tryon2__faceshape-icon">
      <path d="M18,36 C18,36 30,22 30,16 C30,11 26,8 21.5,8 C19,8 18,10 18,10 C18,10 17,8 14.5,8 C10,8 6,11 6,16 C6,22 18,36 18,36 Z" />
    </svg>
  ),
  Oblong: (
    <svg width="32" height="42" viewBox="0 0 36 48" fill="none" stroke="currentColor" strokeWidth="2.5" className="tryon2__faceshape-icon">
      <rect x="6" y="4" width="24" height="40" rx="8" />
    </svg>
  ),
  Diamond: (
    <svg width="32" height="42" viewBox="0 0 36 48" fill="none" stroke="currentColor" strokeWidth="2.5" className="tryon2__faceshape-icon">
      <path d="M18,4 L31,24 L18,44 L5,24 Z" />
    </svg>
  ),
};

/**
 * Frame-thumbnail error handling: try the un-proxied URL once, and if that fails
 * too swap in a neutral glasses outline so the tile never shows a broken image.
 */
function handleThumbError(fallbackUrl: string) {
  return (e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget as HTMLImageElement & { _fb?: boolean };
    if (!el._fb && fallbackUrl) { el._fb = true; el.src = fallbackUrl; return; }
    el.style.display = 'none';
    const parent = el.parentElement;
    if (!parent || parent.querySelector('.tryon2__fallback-svg')) return;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 100 40');
    svg.setAttribute('class', 'tryon2__fallback-svg');
    svg.innerHTML = '<path d="M15,20 C15,12 35,12 35,20 C35,28 15,28 15,20 Z M65,20 C65,12 85,12 85,20 C85,28 65,28 65,20 Z M35,18 Q50,13 65,18 M15,18 Q5,12 2,12 M85,18 Q95,12 98,12"/>';
    parent.appendChild(svg);
  };
}

function viewBoxMapper(videoW: number, videoH: number) {
  if (!videoW || !videoH) return null;
  const scale = Math.max(VB_W / videoW, VB_H / videoH);
  const dispW = videoW * scale, dispH = videoH * scale;
  const offX = (dispW - VB_W) / 2, offY = (dispH - VB_H) / 2;
  return {
    x: (nx: number) => (1 - nx) * dispW - offX, // mirror for selfie view
    y: (ny: number) => ny * dispH - offY,
  };
}

/**
 * Map the face-oval landmark bounds into the overlay viewBox as an ellipse that
 * hugs the actual face. Accounts for the mirrored selfie view (x → 1-x) and the
 * "slice" object-fit (the shorter video axis is cropped to fill the viewBox).
 * Returns null if landmarks are missing.
 */
function computeFaceEllipse(
  landmarks: { x: number; y: number }[],
  videoW: number,
  videoH: number
): { cx: number; cy: number; rx: number; ry: number } | null {
  if (!landmarks?.length || !videoW || !videoH) return null;
  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (const lm of landmarks) {
    if (lm.x < minX) minX = lm.x;
    if (lm.x > maxX) maxX = lm.x;
    if (lm.y < minY) minY = lm.y;
    if (lm.y > maxY) maxY = lm.y;
  }
  // "slice" scale: cover the viewBox; the larger scale wins, the other axis is cropped.
  const scale = Math.max(VB_W / videoW, VB_H / videoH);
  const dispW = videoW * scale, dispH = videoH * scale;
  const offX = (dispW - VB_W) / 2, offY = (dispH - VB_H) / 2;
  // Mirror x for the selfie view.
  const toX = (nx: number) => (1 - nx) * dispW - offX;
  const toY = (ny: number) => ny * dispH - offY;

  const left = toX(maxX), right = toX(minX); // maxX mirrors to the left edge
  const top = toY(minY), bottom = toY(maxY);
  const cx = (left + right) / 2;
  const cy = (top + bottom) / 2;
  // Pad a touch so the ring sits just outside the face landmarks.
  const rx = Math.abs(right - left) / 2 * 1.18;
  const ry = Math.abs(bottom - top) / 2 * 1.12;
  if (!isFinite(cx) || !isFinite(cy) || rx < 8 || ry < 8) return null;
  return { cx, cy, rx, ry };
}

// MediaPipe landmark indices used to locate the eye region vs a cheek baseline.
const LM_EYE_OUTER_L = 33;
const LM_EYE_OUTER_R = 263;
const LM_EYE_TOP = 159;     // upper eyelid (left eye)
const LM_NOSE_TIP = 1;
const LM_CHEEK_L = 50;
const LM_CHEEK_R = 280;

/**
 * Heuristic glasses detection (no ML model). Glasses frames add strong dark
 * horizontal edges across the eye/brow band; bare skin does not. We sample the
 * eye-bridge band and a cheek band from the live video and compare their
 * "edge + darkness" energy. A large eye-vs-cheek ratio ⇒ likely wearing glasses.
 *
 * This is intentionally conservative (favours NOT flagging) to limit false
 * positives; the camera-start reminder remains as the fallback. ~60–75% typical.
 */
function detectGlasses(
  video: HTMLVideoElement,
  landmarks: { x: number; y: number }[],
  scratch: HTMLCanvasElement
): boolean {
  const W = video.videoWidth, H = video.videoHeight;
  if (!W || !H || !landmarks?.length) return false;

  const eyeL = landmarks[LM_EYE_OUTER_L];
  const eyeR = landmarks[LM_EYE_OUTER_R];
  const eyeTop = landmarks[LM_EYE_TOP];
  const nose = landmarks[LM_NOSE_TIP];
  const cheekL = landmarks[LM_CHEEK_L];
  const cheekR = landmarks[LM_CHEEK_R];
  if (!eyeL || !eyeR || !eyeTop || !nose || !cheekL || !cheekR) return false;

  // Region helper: average per-row horizontal-gradient energy in a band.
  const energy = (x0: number, y0: number, x1: number, y1: number): number => {
    const sx = Math.max(0, Math.floor(Math.min(x0, x1) * W));
    const ex = Math.min(W, Math.ceil(Math.max(x0, x1) * W));
    const sy = Math.max(0, Math.floor(Math.min(y0, y1) * H));
    const ey = Math.min(H, Math.ceil(Math.max(y0, y1) * H));
    const bw = ex - sx, bh = ey - sy;
    if (bw < 4 || bh < 2) return 0;
    scratch.width = bw; scratch.height = bh;
    const ctx = scratch.getContext('2d', { willReadFrequently: true });
    if (!ctx) return 0;
    ctx.drawImage(video, sx, sy, bw, bh, 0, 0, bw, bh);
    let data: Uint8ClampedArray;
    try { data = ctx.getImageData(0, 0, bw, bh).data; } catch { return 0; }
    let sum = 0, n = 0;
    for (let y = 0; y < bh; y++) {
      for (let x = 1; x < bw; x++) {
        const i = (y * bw + x) * 4;
        const p = (i);
        const lum = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
        const pl = ((y * bw + (x - 1)) * 4);
        const lumPrev = 0.299 * data[pl] + 0.587 * data[pl + 1] + 0.114 * data[pl + 2];
        sum += Math.abs(lum - lumPrev) + (255 - lum) * 0.15; // edge + darkness
        n++;
      }
    }
    return n ? sum / n : 0;
  };

  // Eye-bridge band: across both eyes, from brow to just above nose tip.
  const eyeBandTop = Math.min(eyeTop.y, eyeL.y, eyeR.y) - 0.02;
  const eyeBandBot = (Math.max(eyeL.y, eyeR.y) + nose.y) / 2;
  const eyeEnergy = energy(eyeL.x - 0.02, eyeBandTop, eyeR.x + 0.02, eyeBandBot);

  // Cheek baseline band (no frame): below the eyes, between the cheeks.
  const cheekTop = (Math.max(eyeL.y, eyeR.y) + nose.y) / 2 + 0.02;
  const cheekBot = nose.y + 0.06;
  const cheekEnergy = energy(cheekL.x, cheekTop, cheekR.x, cheekBot) || 1;

  return eyeEnergy / cheekEnergy > 1.9;
}

const LiveTryonStage: React.FC<LiveTryonStageProps> = ({
  products: cachedProducts,
  loadingProducts,
  targetProduct: cachedTarget = null,
  compact = false,
  onBrowseAll,
  onViewDetails,
  studio = false,
}) => {
  // Frames arrive from the session cache with no price or stock. Live ones for every frame come
  // in one shared call each time the stage opens; until then no price shows and nothing can be
  // bought. The target is priced the same way, since it may not be in the list.
  const { products, state: pricingState, withLive } = useLivePricing(cachedProducts, { all: true });
  const targetProduct = useMemo(() => (cachedTarget ? withLive(cachedTarget) : null), [cachedTarget, withLive]);
  const pricingReady = pricingState === 'live';

  const videoRef = useRef<HTMLVideoElement>(null);
  const arCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);

  const selectedProductRef = useRef<Product | null>(null);
  const detectingRef = useRef(false);
  const lastDetectRef = useRef(0);
  const arRef = useRef<AREngine | null>(null);
  // Offscreen canvas + throttle for the eye-region glasses heuristic.
  const sampleCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastGlassesCheckRef = useRef(0);
  // Face-shape detection: throttle + rolling-mode smoother to avoid label flicker.
  const lastShapeCheckRef = useRef(0);
  const shapeSmootherRef = useRef(new FaceShapeSmoother(14));
  const autoSelectedRef = useRef(false);
  // `studio` is read inside renderLoop, which is memoised with empty deps.
  const studioRef = useRef(studio);
  studioRef.current = studio;

  const { addToCart } = useCart();
  const { user, openLogin } = useAuth();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const org = useOrg();
  const showPdMeasurement = org?.extra_fields?.pd_measurement === true;

  // Native fullscreen on the camera viewport. The WebGL canvas is sized from its
  // container, so re-run the AR engine's resize once the transition has settled.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const stageWrapperRef = useRef<HTMLDivElement>(null);

  const toggleFullscreen = () => {
    if (!stageWrapperRef.current) return;
    if (!document.fullscreenElement) {
      stageWrapperRef.current.requestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch(() => undefined);
    } else {
      document.exitFullscreen()
        .then(() => setIsFullscreen(false))
        .catch(() => undefined);
    }
  };

  /**
   * Leave fullscreen and resolve once the browser has actually exited.
   *
   * The login modal is mounted at the app root (app/providers.tsx), outside the
   * element passed to requestFullscreen — so while fullscreen is active the
   * browser paints only that element's subtree and the modal is invisible. Any
   * flow that has to prompt for login must drop out of fullscreen first.
   */
  const exitFullscreenForModal = useCallback(async () => {
    if (typeof document === 'undefined' || !document.fullscreenElement) return;
    try {
      await document.exitFullscreen();
    } catch {
      /* already exiting, or the browser refused — fall through either way */
    }
    setIsFullscreen(false);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => { arRef.current?.resize(); }, 150);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const [cameraOn, setCameraOn] = useState(false);
  const [engineReady, setEngineReady] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Id of the targetProduct already pushed into `selectedId`, so the auto-select
  // effect below applies a given target once instead of on every re-run.
  const appliedTargetRef = useRef<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-select the target product or the first available product in the catalog.
  //
  // The target is applied ONCE per target id, tracked in appliedTargetRef. This
  // effect depends on `selectedId`, so re-applying it on every run would undo the
  // user's own choice: picking a frame from the strip sets `selectedId`, which
  // re-runs this effect, which would snap the selection straight back to the
  // target ("Your pick") and make the grid appear frozen. Keying on the id (not
  // object identity) matters because the parent re-derives `targetProduct` from a
  // freshly loaded catalogue, which changes the object but not the product.
  useEffect(() => {
    if (targetProduct) {
      if (appliedTargetRef.current !== targetProduct.id) {
        appliedTargetRef.current = targetProduct.id;
        setSelectedId(targetProduct.id);
        selectedProductRef.current = targetProduct;
      }
    } else if (products && products.length > 0) {
      appliedTargetRef.current = null;
      if (selectedId === null || !products.some((p) => p.id === selectedId)) {
        setSelectedId(products[0].id);
        selectedProductRef.current = products[0];
      }
    } else {
      appliedTargetRef.current = null;
      setSelectedId(null);
      selectedProductRef.current = null;
    }
  }, [products, targetProduct, selectedId]);

  // Default-view positioning guidance:
  //  'left' / 'right' = head turned that way, 'center' = face off-centre,
  //  null = good (centered + straight) or no face.
  type Guidance = 'left' | 'right' | 'center' | null;
  const [headTurn, setHeadTurn] = useState<Guidance>(null);
  // Distance/alignment guidance for the default oval: 'far'/'near' when the face is
  // the wrong size in frame; null when distance is good.
  const [fitDist, setFitDist] = useState<'far' | 'near' | null>(null);
  // True when facing straight, centered AND at a good distance — the oval goes green
  // and face-shape sampling is allowed only then (for a reliable, consistent read).
  const goodFitRef = useRef(false);
  const [fitOk, setFitOk] = useState(false);
  const isPortrait = typeof window !== 'undefined' ? window.innerHeight > window.innerWidth : false;
  const fixedOval = isPortrait
    ? { cx: 320, cy: 175, rx: 60, ry: 85 }
    : { cx: 320, cy: 175, rx: 90, ry: 125 };
  // One-time "remove your glasses" notice shown when the camera starts (fallback).
  const [showRemoveGlasses, setShowRemoveGlasses] = useState(false);

  // PD Measurement State
  const [isMeasuringPD, setIsMeasuringPD] = useState(false);
  const [pdMessage, setPdMessage] = useState<string | null>(null);
  const [measuredPdResult, setMeasuredPdResult] = useState<number | null>(null);
  const [measuredLeftPdResult, setMeasuredLeftPdResult] = useState<number | null>(null);
  const [measuredRightPdResult, setMeasuredRightPdResult] = useState<number | null>(null);
  const [isPdSelectionOpen, setIsPdSelectionOpen] = useState(false);
  const [selectedPdType, setSelectedPdType] = useState<'single' | 'dual'>('single');
  const pdActiveRef = useRef(false);
  const pdLibRef = useRef<PDMeasurementLib | null>(null);

  // Heuristic auto-detection: true if the eye region looks like it has glasses.
  const [glassesDetected, setGlassesDetected] = useState(false);

  // Studio: once the face has been held in a good fit for a beat, fade the
  // alignment guide away so it doesn't sit on top of the try-on. It comes back
  // as soon as alignment is lost.
  const [hideGuideOval, setHideGuideOval] = useState(false);
  useEffect(() => {
    if (fitOk) {
      const timer = setTimeout(() => setHideGuideOval(true), 1000);
      return () => clearTimeout(timer);
    } else {
      setHideGuideOval(false);
    }
  }, [fitOk]);

  // Landmark-derived face ellipse in the 640×360 overlay viewBox. Null until a
  // face is found → both ovals fall back to the fixed centered ellipse.
  type Ellipse = { cx: number; cy: number; rx: number; ry: number };
  const [faceEllipse, setFaceEllipse] = useState<Ellipse | null>(null);
  // Smoothed, detected face shape (null until the user runs detection).
  const [faceShape, setFaceShape] = useState<FaceShape | null>(null);
  const [faceShapeCollapsed, setFaceShapeCollapsed] = useState(false);
  const [faceShapeClosed, setFaceShapeClosed] = useState(false);
  // On-demand face-shape detection: true while sampling after the user taps
  // "Detect Face Shape". Detection only runs during this window — never passively.
  const [detectingShape, setDetectingShape] = useState(false);
  const detectingShapeRef = useRef(false);
  // Live face landmarks mapped to the 640×360 overlay viewBox (mirrored), shown as
  // a thin white mesh while detection is running. Empty when not detecting.
  const [meshPts, setMeshPts] = useState<{ x: number; y: number }[]>([]);
  // Live classifier ratios shown as a debug readout during detection (for calibration).
  const [debugRatios, setDebugRatios] = useState<{ r1: number; jaw: number; forehead: number } | null>(null);
  // Active frame-shape filter (a frame_shape_id) set by tapping a recommended tag.
  // null = no filter. Applied to the frame grid on the right.
  const [shapeFilter, setShapeFilter] = useState<number | null>(null);

  // Refine-fit mode (shows the oval + alignment guidance).
  const [refineFit, setRefineFit] = useState(false);
  const refineFitRef = useRef(false);
  const [fitStage, setFitStage] = useState<FitStage>('searching');
  const fitStageRef = useRef<FitStage>('searching');
  // Ring fill progress 0→1 while holding a perfect fit; pauses (holds) on loss.
  const [fitProgress, setFitProgress] = useState(0);
  // Set once the ring completes — shows the success confirmation before returning.
  const [fitConfirmed, setFitConfirmed] = useState(false);

  const setRefine = (v: boolean) => {
    refineFitRef.current = v;
    setRefineFit(v);
    if (!v) { setFitProgress(0); setFitConfirmed(false); }
  };
  const setFit = (s: FitStage) => { fitStageRef.current = s; setFitStage(s); };

  const recommendMode = !!targetProduct;


  useEffect(() => {
    preloadEngine()
      .then(() => setEngineReady(true))
      .catch(() => console.error('Failed to load face tracking model'));
  }, []);

  const autoStarted = useRef(false);
  useEffect(() => {
    if (engineReady && !autoStarted.current) {
      autoStarted.current = true;
      startCamera().catch(err => {
        console.warn("Auto-start failed", err);
      });
    }
    return () => { };
  }, [engineReady]);


  // ── Live AR render loop ──
  const renderLoop = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(renderLoop);
      return;
    }

    const now = performance.now();
    if (arRef.current && !detectingRef.current && now - lastDetectRef.current > 40) {
      detectingRef.current = true;
      lastDetectRef.current = now;
      detectRaw(video, now)
        .then((det) => {
          if (det) {
            arRef.current?.update(det, video.videoWidth, video.videoHeight);

            // Landmark-derived face ellipse for the refine-fit mask cutout.
            if (det.landmarks?.length) {
              const el = computeFaceEllipse(det.landmarks, video.videoWidth, video.videoHeight);
              if (el) setFaceEllipse(el);
            }

            // Default-view positioning guidance.
            let guide: Guidance = null;
            // 1) Head turn from the head-pose matrix yaw (column-major 4×4).
            //    Labels are flipped because the camera view is mirrored (selfie).
            if (det.matrix && det.matrix.length >= 16) {
              const yaw = Math.atan2(-det.matrix[2], det.matrix[10]); // radians
              const YAW_LIMIT = 0.31; // ~18°
              if (yaw > YAW_LIMIT) guide = 'left';
              else if (yaw < -YAW_LIMIT) guide = 'right';
            }
            // 2) If facing straight, check centering AND distance (face size in frame).
            let dist: 'far' | 'near' | null = null;
            let centered = true;
            if (det.landmarks?.length) {
              let minX = 1, maxX = 0, minY = 1, maxY = 0;
              for (const lm of det.landmarks) {
                if (lm.x < minX) minX = lm.x;
                if (lm.x > maxX) maxX = lm.x;
                if (lm.y < minY) minY = lm.y;
                if (lm.y > maxY) maxY = lm.y;
              }
              const cx = (minX + maxX) / 2;
              const cy = (minY + maxY) / 2;
              const h = maxY - minY;
              centered = Math.abs(cx - 0.5) < 0.14 && Math.abs(cy - 0.5) < 0.14;
              if (!guide && !centered) guide = 'center';
              // Distance from face height in frame (only meaningful when facing straight).
              if (h < 0.46) dist = 'far';
              else if (h > 0.82) dist = 'near';
            }
            setHeadTurn(guide);
            setFitDist(guide ? null : dist); // hide distance hint while turned

            // Perfect fit: straight, centered, good distance. Gates the green oval
            // and face-shape sampling for a reliable read.
            goodFitRef.current = !guide && centered && dist === null;
            setFitOk(goodFitRef.current);

            // Face-shape detection — ONLY while the user is actively running it
            // (after tapping "Detect Face Shape"), and only when looking roughly
            // straight ahead. Samples accumulate in the smoother; the result is
            // locked in by the detection timer below.
            if (detectingShapeRef.current && det.landmarks?.length) {
              // Draw the live face mesh (downsampled) mapped into the overlay viewBox.
              const m = viewBoxMapper(video.videoWidth, video.videoHeight);
              if (m) {
                const pts: { x: number; y: number }[] = [];
                // Denser sampling (every 2nd point) for fuller face coverage.
                for (let i = 0; i < det.landmarks.length; i += 2) {
                  const lm = det.landmarks[i];
                  pts.push({ x: m.x(lm.x), y: m.y(lm.y) });
                }
                // Always include the full face-oval contour so the outline is traced.
                for (const idx of FACE_OVAL_IDX) {
                  const lm = det.landmarks[idx];
                  if (lm) pts.push({ x: m.x(lm.x), y: m.y(lm.y) });
                }
                // Add the ESTIMATED hairline + forehead band (MediaPipe stops near the
                // brow). Extrapolate above the oval top to the trichion, fanning across
                // the forehead width so the top of the face is visibly covered.
                const top = det.landmarks[10], chin = det.landmarks[152];
                const fL = det.landmarks[54], fR = det.landmarks[284];
                if (top && chin) {
                  const dy = (chin.y - top.y) * 0.18;
                  for (let k = 1; k <= 5; k++) {
                    const t = k / 5;
                    const y = top.y - dy * t;
                    // sweep a few x positions across the forehead band
                    const xs = fL && fR ? [fL.x, (fL.x + top.x) / 2, top.x, (fR.x + top.x) / 2, fR.x] : [top.x];
                    for (const x of xs) pts.push({ x: m.x(x), y: m.y(y) });
                  }
                }
                setMeshPts(pts);
              }
              // Sample the classifier on a perfect fit (straight + centered + good
              // distance) so readings stay consistent. In studio mode an explicit
              // scan also samples during its countdown, so a near-aligned user
              // still gets a result instead of a stalled "Detecting…".
              if ((goodFitRef.current || (studioRef.current && detectingShapeRef.current))
                && now - lastShapeCheckRef.current > 120) {
                lastShapeCheckRef.current = now;
                const aspect = video.videoWidth / video.videoHeight || 1;
                const res = detectFaceShape(det.landmarks as any, aspect);
                if (res) {
                  shapeSmootherRef.current.push(res.shape);
                  setDebugRatios({ r1: res.r1, jaw: res.jaw, forehead: res.forehead });
                }
              }
            }

            // Glasses heuristic — throttled to ~1/sec (pixel sampling is costly).
            // Only meaningful when facing straight; skip while turned.
            if (!guide || guide === 'center') {
              if (now - lastGlassesCheckRef.current > 1000) {
                lastGlassesCheckRef.current = now;
                if (!sampleCanvasRef.current) sampleCanvasRef.current = document.createElement('canvas');
                try {
                  const wears = detectGlasses(video, det.landmarks as any, sampleCanvasRef.current);
                  setGlassesDetected(wears);
                } catch { /* ignore sampling errors */ }
              }
            }

            // Only compute fit guidance while refining.
            if (refineFitRef.current) {
              const lms = det.landmarks;
              if (lms && lms.length > 0) {
                let minX = 1, maxX = 0, minY = 1, maxY = 0;
                for (const lm of lms) {
                  if (lm.x < minX) minX = lm.x;
                  if (lm.x > maxX) maxX = lm.x;
                  if (lm.y < minY) minY = lm.y;
                  if (lm.y > maxY) maxY = lm.y;
                }
                const cx = (minX + maxX) / 2;
                const cy = (minY + maxY) / 2;
                const w = maxX - minX;
                const h = maxY - minY;
                const centered = Math.abs(cx - 0.5) < 0.14 && Math.abs(cy - 0.5) < 0.14;
                if (!centered) setFit('searching');
                else if (w < 0.12 || h < 0.28) setFit('far');
                else if (w < 0.16 || h < 0.34) setFit('almost');
                else if (w > 0.55 || h > 0.92) setFit('far');
                else setFit('ok');
              } else {
                setFit('searching');
              }
            }
          } else {
            // No face detected → not "turned"; the dotted guide invites the user in.
            setHeadTurn(null);
            setFitDist(null);
            goodFitRef.current = false;
            setFitOk(false);
            if (refineFitRef.current) setFit('searching');
          }
        })
        .catch(() => undefined)
        .finally(() => { detectingRef.current = false; });
    }

    rafRef.current = requestAnimationFrame(renderLoop);
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 1280, height: 720 },
      });
      streamRef.current = stream;
      setCameraOn(true);
      // Remind the user to take off any glasses they're wearing (auto-hides).
      setShowRemoveGlasses(true);
      setTimeout(() => setShowRemoveGlasses(false), 5000);
      setTimeout(async () => {
        const video = videoRef.current;
        const arCanvas = arCanvasRef.current;
        if (!video || !arCanvas) return;
        video.srcObject = stream;
        video.onloadedmetadata = async () => {
          await video.play().catch(() => undefined);
          if (!arRef.current) {
            arRef.current = await createAREngine(arCanvas, video);
          }
          arRef.current.resize();
          rafRef.current = requestAnimationFrame(renderLoop);
          if (selectedProductRef.current) {
            selectFrame(selectedProductRef.current);
          }
        };
      }, 50);
    } catch {
      alert('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (arRef.current) {
      arRef.current.clearModel();
      arRef.current = null;
    }
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const selectFrame = useCallback(async (product: Product) => {
    setSelectedId(product.id);
    selectedProductRef.current = product;
    try {
      if (!arRef.current && arCanvasRef.current && videoRef.current) {
        arRef.current = await createAREngine(arCanvasRef.current, videoRef.current);
      }
      if (!arRef.current) return;
      arRef.current.resize();
      const opacity = product.product_attr?.opacity ? parseFloat(product.product_attr.opacity) : 1;
      const primary = proxyFrameUrl(product);
      const fallback = resolveFrameUrl(product);
      try {
        if (!primary) throw new Error('no url');
        await arRef.current.setPhotoFrame(primary, opacity);
      } catch {
        if (fallback) await arRef.current.setPhotoFrame(fallback, opacity);
        else throw new Error('no frame');
      }
    } catch {
      alert('Could not load that frame.');
    }
  }, []);

  // On-demand face-shape detection. Samples ONLY accumulate while the fit is
  // perfect (green oval), so we poll until the smoother has a confident read,
  // capping at ~9s so it never hangs if the user never aligns.
  const detectTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runFaceShapeDetection = useCallback(() => {
    if (!cameraOn) return;
    if (detectTimerRef.current) clearInterval(detectTimerRef.current);
    shapeSmootherRef.current.reset();
    lastShapeCheckRef.current = 0;
    setDebugRatios(null);
    setFaceShapeClosed(false);
    setFaceShapeCollapsed(false);
    setDetectingShape(true);
    detectingShapeRef.current = true;

    const start = performance.now();
    const finish = () => {
      if (detectTimerRef.current) { clearInterval(detectTimerRef.current); detectTimerRef.current = null; }
      detectingShapeRef.current = false;
      setDetectingShape(false);
      setMeshPts([]);
      if (shapeSmootherRef.current.ready) {
        setFaceShape(shapeSmootherRef.current.mode());
        // Collapse to the compact chip so the result doesn't cover the face; the
        // user can tap it to expand the recommendations/filters.
        // setFaceShapeCollapsed(true);
      }
    };
    detectTimerRef.current = setInterval(() => {
      const elapsed = performance.now() - start;
      // Stop once we have a solid window of samples, or after the safety cap.
      if (shapeSmootherRef.current.ready || elapsed > 9000) finish();
    }, 200);
  }, [cameraOn]);

  useEffect(() => () => { if (detectTimerRef.current) clearInterval(detectTimerRef.current); }, []);


  const handleMeasurePD = async (type: 'single' | 'dual') => {
    if (!videoRef.current) {
      alert("Camera not ready.");
      return;
    }
    pdActiveRef.current = true;
    setIsMeasuringPD(true);
    setPdMessage("Initializing PD Measurement...");
    setIsPdSelectionOpen(false);
    setMeasuredPdResult(null);
    setMeasuredLeftPdResult(null);
    setMeasuredRightPdResult(null);

    pdLibRef.current = new PDMeasurementLib(videoRef.current, {
      pdType: type,
      onStatus: (status) => { if (pdActiveRef.current) setPdMessage(status); },
      onDetected: (result: any) => {
        setMeasuredPdResult(result.pd);
        if (result.leftPd) setMeasuredLeftPdResult(result.leftPd);
        if (result.rightPd) setMeasuredRightPdResult(result.rightPd);
        
        console.log("PD Measured:", result);
        if ((window as any).onPdComplete) (window as any).onPdComplete(result.pd);
        
        localStorage.setItem('measuredPD', result.pd.toString());
        if (result.leftPd) localStorage.setItem('measuredLeftPD', result.leftPd.toString());
        if (result.rightPd) localStorage.setItem('measuredRightPD', result.rightPd.toString());
        cancelPD();
      },
      onError: (err) => {
        alert("Measurement Error: " + err);
        cancelPD();
      }
    });

    pdLibRef.current.start();
  };

  const cancelPD = () => {
    if (pdLibRef.current) {
      pdLibRef.current.stop();
      pdLibRef.current = null;
    }
    pdActiveRef.current = false;
    setIsMeasuringPD(false);
    setPdMessage(null);
  };

  const downloadSnapshot = () => {
    const ar = arRef.current;
    if (!ar) { alert('Start the camera first.'); return; }
    const dataUrl = ar.snapshot();
    if (!dataUrl || dataUrl.length < 100) { alert('Could not capture the image. Try again.'); return; }
    const link = document.createElement('a');
    link.download = `tryon-${selectedId ?? 'live'}-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
  };

  // Frames shown in the side strip.
  //  - Compact (modal): the picked frame + only its closest similar frames, so the
  //    strip is a focused "you may also like" set rather than the whole catalogue.
  //  - Full studio: the picked frame first, then the entire ranked catalogue to browse.
  const SIMILAR_COUNT = 7;
  const stripProducts = useMemo(() => {
    if (!targetProduct) return products;
    // Compact: only genuinely-related frames (shared shape/type/category), capped.
    // Full studio: the whole ranked catalogue so everything stays browsable.
    const similar = compact
      ? similarFrames(targetProduct, products, SIMILAR_COUNT, true)
      : similarFrames(targetProduct, products, products.length);
    return [targetProduct, ...similar];
  }, [targetProduct, products, compact]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedId) ?? targetProduct ?? null,
    [products, selectedId, targetProduct]
  );

  // While refining, the ring fills over ~4s ONLY while the fit is perfect.
  // Losing the fit pauses (holds) the fill; regaining it resumes from where it
  // left off. At 100% we auto-return to the live view. Uses a rAF loop reading
  // the live fit via fitStageRef so it never works off a stale value.
  useEffect(() => {
    if (!refineFit) return;
    const FILL_MS = 4000;
    let raf = 0;
    let confirmTimer: ReturnType<typeof setTimeout> | undefined;
    let last = performance.now();
    let progress = 0;
    let done = false;
    setFitProgress(0);
    setFitConfirmed(false);

    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      if (fitStageRef.current === 'ok') {
        progress = Math.min(1, progress + dt / FILL_MS);
        setFitProgress(progress);
        if (progress >= 1) {
          // Ring complete — show the success confirmation, then return after 2s.
          done = true;
          setFitConfirmed(true);
          confirmTimer = setTimeout(() => {
            setRefine(false);
            setFit('searching');
          }, 20000);
          return;
        }
      }
      // not 'ok' → hold current progress (no decrement)
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); if (confirmTimer) clearTimeout(confirmTimer); void done; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refineFit]);

  // Auto-apply the target frame to the live video once the camera is on.
  useEffect(() => {
    if (cameraOn && !autoSelectedRef.current) {
      const activeProduct = selectedProduct || targetProduct || (products && products[0]);
      if (activeProduct) {
        autoSelectedRef.current = true;
        void selectFrame(activeProduct);
      }
    }
  }, [cameraOn, selectedProduct, targetProduct, products, selectFrame]);

  // Arrow keys switch frames.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!cameraOn || stripProducts.length === 0) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const i = stripProducts.findIndex((p) => p.id === selectedId);
        void selectFrame(stripProducts[i === -1 ? 0 : (i + 1) % stripProducts.length]);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const i = stripProducts.findIndex((p) => p.id === selectedId);
        const prev = i === -1 ? stripProducts.length - 1 : (i - 1 + stripProducts.length) % stripProducts.length;
        void selectFrame(stripProducts[prev]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cameraOn, selectedId, stripProducts, selectFrame]);

  // Live stock decides, as on the product page. Before this, try-on let an out-of-stock frame
  // into the cart.
  const canBuy = pricingReady && !!selectedProduct && (selectedProduct.quantity ?? 0) > 0;

  const handleAddToCart = async () => {
    if (!selectedProduct || !canBuy) return;
    if (!user) { await exitFullscreenForModal(); openLogin(); return; }
    setAdding(true);
    const ok = await addToCart(selectedProduct.id, 1);
    setAdding(false);
    if (ok) { setAdded(true); setTimeout(() => setAdded(false), 2000); }
  };

  const handleWishlistToggle = async () => {
    if (!selectedProduct) return;
    if (!user) { await exitFullscreenForModal(); openLogin(); return; }
    await toggleWishlist(selectedProduct.id);
  };
  const selectedWishlisted = selectedProduct ? isWishlisted(selectedProduct.id) : false;

  const fitMessage =
    fitConfirmed ? 'Perfect fit! If you’re wearing glasses, please remove them for an accurate try-on.'
      : fitStage === 'searching' ? 'Position your face in the oval and face forwards.'
        : fitStage === 'far' ? 'Move closer.'
          : fitStage === 'almost' ? 'Almost there.'
            : 'Perfect fit!';

  return (
    <div className={`tryon2 ${compact ? 'tryon2--compact' : 'tryon2--unified'}`}>
      {/* Camera stage */}
      <div className="tryon2__stage" style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div
          ref={stageWrapperRef}
          className={`tryon2__viewport ${cameraOn ? 'tryon2__viewport--camera-on' : ''} ${isFullscreen ? 'tryon2__viewport--fullscreen' : ''}`}
          style={!compact ? { width: '100%', height: '100%', aspectRatio: 'auto' } : undefined}
        >
          <video ref={videoRef} autoPlay playsInline muted style={{ display: 'none' }} />
          <canvas
            ref={arCanvasRef}
            className="tryon2__canvas"
            style={{ display: cameraOn ? 'block' : 'none', width: '100%', height: '100%', objectFit: 'cover' }}
          />
{isPdSelectionOpen && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)'
            }}>
              <div style={{
                background: 'var(--color-bg, #ffffff)', padding: '24px', borderRadius: '16px',
                width: '90%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                border: '1px solid var(--color-border, #e5e7eb)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: 'var(--color-text, #111827)' }}>Select Your Goal</h2>
                  <button onClick={() => setIsPdSelectionOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted, #6b7280)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                  </button>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', padding: '16px', border: selectedPdType === 'single' ? '2px solid var(--color-primary, #0d0d0d)' : '2px solid var(--color-border, #e5e7eb)', backgroundColor: selectedPdType === 'single' ? 'rgba(0,0,0,0.02)' : 'transparent', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, padding: '4px 8px', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '6px', color: 'var(--color-text, #111827)' }}>Standard</span>
                      <input type="radio" name="pdTypeStage" value="single" checked={selectedPdType === 'single'} onChange={() => setSelectedPdType('single')} style={{ width: '20px', height: '20px', accentColor: 'var(--color-primary, #0d0d0d)' }} />
                    </div>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px 0', color: 'var(--color-text, #111827)' }}>Single PD (Binocular)</h3>
                    <p style={{ fontSize: '13px', margin: 0, color: 'var(--color-text-muted, #6b7280)' }}>Measures the total distance from one pupil to the other.</p>
                  </label>
                  
                  <label style={{ display: 'flex', flexDirection: 'column', padding: '16px', border: selectedPdType === 'dual' ? '2px solid var(--color-primary, #0d0d0d)' : '2px solid var(--color-border, #e5e7eb)', backgroundColor: selectedPdType === 'dual' ? 'rgba(0,0,0,0.02)' : 'transparent', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, padding: '4px 8px', backgroundColor: 'var(--color-bg-secondary, #f3f4f6)', borderRadius: '6px', color: 'var(--color-text, #111827)' }}>High Precision</span>
                      <input type="radio" name="pdTypeStage" value="dual" checked={selectedPdType === 'dual'} onChange={() => setSelectedPdType('dual')} style={{ width: '20px', height: '20px', accentColor: 'var(--color-primary, #0d0d0d)' }} />
                    </div>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0 0 8px 0', color: 'var(--color-text, #111827)' }}>Dual PD (Monocular)</h3>
                    <p style={{ fontSize: '13px', margin: 0, color: 'var(--color-text-muted, #6b7280)' }}>Measures each eye individually from the pupil to the nose bridge.</p>
                  </label>
                </div>
                
                <button 
                  onClick={() => handleMeasurePD(selectedPdType)}
                  style={{ width: '100%', marginTop: '20px', padding: '12px', backgroundColor: 'var(--color-primary, #0d0d0d)', color: 'white', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Proceed to Measurement
                </button>
              </div>
            </div>
          )}
          {(measuredPdResult !== null || (measuredLeftPdResult !== null && measuredRightPdResult !== null)) && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)'
            }}>
              <div style={{
                background: 'var(--color-bg, #ffffff)', padding: '24px', borderRadius: '16px',
                width: '90%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                border: '1px solid var(--color-border, #e5e7eb)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: '0 0 8px 0', color: 'var(--color-text, #111827)' }}>
                  Measurement Complete
                </h2>
                <div style={{
                  background: '#f0fdf4', color: '#16a34a', padding: '16px', borderRadius: '50%',
                  margin: '16px 0'
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <p style={{ fontSize: '15px', color: 'var(--color-text-muted, #6b7280)', marginBottom: '8px' }}>
                  Your Pupillary Distance (PD) is
                </p>
                
                {measuredLeftPdResult && measuredRightPdResult ? (
                  <div style={{ display: 'flex', gap: '32px', marginBottom: '24px' }}>
                    <div>
                      <div style={{ fontSize: '13px', color: 'var(--color-text-muted, #6b7280)', marginBottom: '4px' }}>Left Eye</div>
                      <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--color-primary, #0d0d0d)', lineHeight: 1 }}>
                        {measuredLeftPdResult}<span style={{ fontSize: '18px', fontWeight: 'normal', color: 'var(--color-text-muted, #6b7280)' }}> mm</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', color: 'var(--color-text-muted, #6b7280)', marginBottom: '4px' }}>Right Eye</div>
                      <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--color-primary, #0d0d0d)', lineHeight: 1 }}>
                        {measuredRightPdResult}<span style={{ fontSize: '18px', fontWeight: 'normal', color: 'var(--color-text-muted, #6b7280)' }}> mm</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '48px', fontWeight: 'bold', color: 'var(--color-primary, #0d0d0d)', marginBottom: '24px', lineHeight: 1 }}>
                    {measuredPdResult}
                    <span style={{ fontSize: '20px', fontWeight: 'normal', color: 'var(--color-text-muted, #6b7280)' }}> mm</span>
                  </div>
                )}

                <p style={{ fontSize: '13px', color: 'var(--color-text-light, #9ca3af)', marginBottom: '24px', lineHeight: '1.5' }}>
                  We've saved this value. It will be automatically applied when you select lenses during checkout.
                </p>
                <button
                  onClick={() => {
                    setMeasuredPdResult(null);
                    setMeasuredLeftPdResult(null);
                    setMeasuredRightPdResult(null);
                  }}
                  style={{
                    width: '100%', padding: '12px', background: 'var(--color-primary, #0d0d0d)',
                    color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px',
                    fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s'
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          )}


          {!cameraOn && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: '#000000', color: 'white'
            }}>
              <p style={{ fontSize: '20px', fontWeight: 500, marginBottom: '20px' }}>Camera Off</p>
              <button
                onClick={startCamera}
                disabled={!engineReady}
                style={{
                  background: 'white', color: 'black', padding: '12px 24px',
                  borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center',
                  cursor: engineReady ? 'pointer' : 'not-allowed', border: 'none',
                  opacity: engineReady ? 1 : 0.7
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                {!engineReady ? 'Loading...' : 'Start Camera'}
              </button>
            </div>
          )}

          {/* Selected Product Badge at Top-Left */}
          {cameraOn && !refineFit && selectedProduct && (
            <div style={{
              position: 'absolute', top: '16px', left: '16px', zIndex: 20,
              display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start',
              pointerEvents: 'none'
            }}>
              <div style={{
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                color: 'white', padding: '8px 14px', borderRadius: '8px', pointerEvents: 'auto',
                maxWidth: '280px', textAlign: 'left', boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <h3 style={{ margin: 0, fontWeight: 600, fontSize: '13px', whiteSpace: 'normal', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedProduct.brand && String(selectedProduct.brand).toLowerCase() !== 'nan' ? `${selectedProduct.brand} ` : ''}
                  {selectedProduct.product_name || 'Selected Frame'}
                </h3>
                {pricingReady && (
                <div style={{ fontSize: '11px', color: '#86efac', fontWeight: 600, marginTop: '2px' }}>
                  ₹{Math.round(selectedProduct.selling_price).toLocaleString()}
                  {selectedProduct.discount ? (
                    <span style={{ color: '#d1d5db', marginLeft: '6px', textDecoration: 'line-through' }}>
                      ₹{Math.round(selectedProduct.selling_price / (1 - selectedProduct.discount / 100)).toLocaleString()}
                    </span>
                  ) : null}
                </div>
                )}
              </div>
            </div>
          )}

          {cameraOn && refineFit && (
            <>
              {/* Refine-fit oval + guidance */}
              {(() => {
                const filling = fitStage === 'ok';
                const baseColor = !filling && fitProgress > 0 ? '#ef4444' : 'rgba(255,255,255,0.45)';
                const o = fixedOval;
                const ringWidth = 3;
                const fillColor = '#81C784';
                const scrim = 'rgba(0, 0, 0, 0.4)';
                return (
                  <svg className="tryon2__oval" viewBox="0 0 640 360" preserveAspectRatio="xMidYMid slice" style={{ zIndex: 10 }}>
                    <defs>
                      <mask id="tryon2-mask">
                        <rect x="0" y="0" width="640" height="360" fill="white" />
                        <ellipse cx={o.cx} cy={o.cy} rx={o.rx} ry={o.ry} fill="black" />
                      </mask>
                    </defs>
                    <rect x="0" y="0" width="640" height="360" fill={scrim} mask="url(#tryon2-mask)" />
                    <ellipse
                      cx={o.cx} cy={o.cy} rx={o.rx} ry={o.ry} fill="none"
                      stroke={baseColor} strokeWidth={ringWidth} strokeLinecap="round"
                    />
                    <ellipse
                      cx={o.cx} cy={o.cy} rx={o.rx} ry={o.ry} fill="none"
                      stroke={fillColor} strokeWidth={ringWidth} strokeLinecap="round"
                      pathLength={100}
                      strokeDasharray={100}
                      strokeDashoffset={100 * (1 - fitProgress)}
                      style={{ transition: 'stroke-dashoffset 0.1s linear' }}
                    />
                  </svg>
                );
              })()}
              <div className={`tryon2__fit-msg ${fitConfirmed ? 'tryon2__fit-msg--ok' : ''}`} style={{ zIndex: 20 }}>{fitMessage}</div>
              <button className="tryon2__back" onClick={() => setRefine(false)} aria-label="Done refining" style={{ zIndex: 20 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
            </>
          )}

          {cameraOn && !refineFit && (
            <>
              {!hideGuideOval && (() => {
                const turned = headTurn === 'left' || headTurn === 'right';
                const offCenter = headTurn === 'center';
                const wrongDist = fitDist !== null;
                const perfect = fitOk;
                const ringColor = perfect ? '#22c55e'
                  : turned ? '#ef4444'
                    : (offCenter || wrongDist) ? '#f59e0b'
                      : 'rgba(255,255,255,0.9)';
                const o = fixedOval;
                return (
                  <svg className={`tryon2__guide ${perfect ? 'tryon2__guide--ok' : ''}`} viewBox="0 0 640 360" preserveAspectRatio="xMidYMid slice" style={{ zIndex: 10 }}>
                    <ellipse
                      cx={o.cx} cy={o.cy} rx={o.rx} ry={o.ry} fill="none"
                      stroke={ringColor}
                      strokeWidth={perfect ? 5 : 4}
                      strokeDasharray={turned || perfect ? 'none' : '6 12'}
                      strokeLinecap="round"
                    />
                  </svg>
                );
              })()}

              {detectingShape && meshPts.length > 0 && (
                <svg className={`tryon2__mesh ${fitOk ? 'tryon2__mesh--ok' : ''}`} viewBox="0 0 640 360" preserveAspectRatio="xMidYMid slice" style={{ zIndex: 10 }}>
                  {meshPts.map((p, i) => (
                    <circle
                      key={i}
                      cx={p.x}
                      cy={p.y}
                      r={0.9}
                      style={{ animationDelay: `${(p.y / VB_H) * 1.2}s` }}
                    />
                  ))}
                </svg>
              )}

              {headTurn === 'center' && (
                <div className="tryon2__turn-msg tryon2__turn-msg--warn" style={{ zIndex: 20 }}>
                  Center your face in the oval for the most accurate fit.
                </div>
              )}
              {!headTurn && fitDist && (
                <div className="tryon2__turn-msg tryon2__turn-msg--warn" style={{ zIndex: 20 }}>
                  {fitDist === 'far' ? 'Move closer to fill the oval.' : 'Move back a little.'}
                </div>
              )}
              {(headTurn === 'left' || headTurn === 'right') && (
                <div className="tryon2__turn-msg" style={{ zIndex: 20 }}>
                  Your face is turned {headTurn} — the glasses fit may vary. Please look straight into the camera.
                </div>
              )}

              {/* Face-shape panel */}
              {cameraOn && (detectingShape || faceShape) && !faceShapeClosed && (
                <div className={`tryon2__faceshape ${faceShapeCollapsed ? 'tryon2__faceshape--collapsed' : ''}`} style={{ zIndex: 25 }}>
                  <div
                    className="tryon2__faceshape-header"
                    onClick={() => setFaceShapeCollapsed((prev) => !prev)}
                    title={faceShapeCollapsed ? 'Expand Face Shape Panel' : 'Collapse Face Shape Panel'}
                  >
                    <div className="tryon2__faceshape-header-left">
                      <span className="tryon2__faceshape-eyebrow">Face Shape</span>
                      {faceShapeCollapsed && faceShape && (
                        <span className="tryon2__faceshape-collapsed-name"> ({faceShape})</span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="tryon2__faceshape-close"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFaceShapeClosed(true);
                        setDetectingShape(false);
                        setFaceShape(null);
                        detectingShapeRef.current = false;
                      }}
                      title="Close Face Shape Panel"
                      aria-label="Close Face Shape Panel"
                    >
                      ×
                    </button>
                  </div>

                  {!faceShapeCollapsed && (
                    <div className="tryon2__faceshape-content">
                      {detectingShape ? (
                        <>
                          <span className="tryon2__faceshape-name tryon2__faceshape-name--detecting">Detecting...</span>
                          <p className="tryon2__faceshape-tip">{fitOk ? 'Hold still — reading your face shape.' : 'Align your face in the oval until it turns green.'}</p>
                          {debugRatios && (
                            <code className="tryon2__faceshape-debug">
                              R1 {debugRatios.r1.toFixed(2)} · jaw {debugRatios.jaw.toFixed(2)} · fore {debugRatios.forehead.toFixed(2)}
                            </code>
                          )}
                        </>
                      ) : faceShape ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <div style={{ color: 'var(--color-primary)', flexShrink: 0, opacity: 0.8 }}>
                              {FACE_SHAPE_SVGS[faceShape]}
                            </div>
                            <span className="tryon2__faceshape-name" style={{ margin: 0 }}>{faceShape}</span>
                          </div>
                          <p className="tryon2__faceshape-tip" style={{ marginTop: '4px' }}>{FACE_SHAPE_GUIDANCE[faceShape]}</p>
                          <span className="tryon2__faceshape-rec-label">Recommended styles</span>
                          <div className="tryon2__faceshape-tags">
                            {shapeToRecommendedFrames(faceShape).slice(0, 3).map((id) => (
                              <span
                                key={id}
                                className="tryon2__faceshape-tag tryon2__faceshape-tag--static"
                              >
                                {FRAME_SHAPES[id]}
                              </span>
                            ))}
                          </div>
                          <button className="tryon2__faceshape-btn tryon2__faceshape-btn--ghost" onClick={runFaceShapeDetection}>
                            Re-detect
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="tryon2__faceshape-tip">Find frame styles that suit your face.</p>
                          <button className="tryon2__faceshape-btn" onClick={runFaceShapeDetection}>
                            Detect Face Shape
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Glasses Notice */}
          {cameraOn && (glassesDetected || showRemoveGlasses) && (
            <div className={`tryon2__glasses-notice ${glassesDetected ? 'tryon2__glasses-notice--detected' : ''}`} style={{ zIndex: 25 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="14" r="4" /><circle cx="18" cy="14" r="4" /><line x1="10" y1="13" x2="14" y2="13" /><path d="M2 11l2-2" /><path d="M22 11l-2-2" /></svg>
              <span>
                {glassesDetected
                  ? 'It looks like you’re wearing glasses — please remove them for an accurate try-on.'
                  : 'Wearing glasses? Please remove them for an accurate try-on.'}
              </span>
              <button onClick={() => { setShowRemoveGlasses(false); setGlassesDetected(false); }} aria-label="Dismiss">×</button>
            </div>
          )}


          {pdMessage && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              background: 'rgba(0,0,0,0.7)', color: 'white', padding: '16px 24px', borderRadius: '8px',
              fontSize: '18px', fontWeight: 'bold', zIndex: 50, textAlign: 'center'
            }}>
              {pdMessage}
            </div>
          )}

          {/* Tools Panel */}
          {cameraOn && (
            <div className="tryon2__tools" style={{ bottom: '90px', transition: 'bottom 0.25s ease', zIndex: 20 }}>

              {showPdMeasurement && (
                <button
                  className="tryon2__tool"
                  onClick={isMeasuringPD ? cancelPD : () => setIsPdSelectionOpen(true)}
                  title={isMeasuringPD ? "Cancel PD" : "Measure PD"}
                  aria-label="Measure PD"
                  style={{
                    background: isMeasuringPD ? 'var(--color-primary)' : 'rgba(255,255,255,0.92)',
                    color: isMeasuringPD ? '#ffffff' : '#1f2937'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <line x1="6" y1="8" x2="6" y2="16"></line>
                    <line x1="18" y1="8" x2="18" y2="16"></line>
                  </svg>
                </button>
              )}

              <button className="tryon2__tool" onClick={downloadSnapshot} title="Snapshot" aria-label="Snapshot">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
              </button>
              <button
                className="tryon2__tool"
                onClick={runFaceShapeDetection}
                title="Detect face shape"
                aria-label="Detect face shape"
                style={{
                  background: detectingShape ? 'var(--color-primary)' : 'rgba(255,255,255,0.92)',
                  color: detectingShape ? '#ffffff' : '#1f2937'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7V5a2 2 0 0 1 2-2h2" />
                  <path d="M17 3h2a2 2 0 0 1 2 2v2" />
                  <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
                  <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
              <button
                className="tryon2__tool"
                onClick={() => {
                  setRefine(!refineFit);
                  setFitStage('searching');
                }}
                title="Refine fit"
                aria-label="Refine fit"
                style={{
                  background: refineFit ? 'var(--color-primary)' : 'rgba(255,255,255,0.92)',
                  color: refineFit ? '#ffffff' : '#1f2937'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="14" r="4" /><circle cx="18" cy="14" r="4" /><line x1="10" y1="13" x2="14" y2="13" /><path d="M21 11l2-2" /><path d="M22 11l-2-2" /></svg>
              </button>
              <button className="tryon2__tool tryon2__tool--close" onClick={() => { if (cameraOn) { stopCamera(); setCameraOn(false); } else { startCamera(); setCameraOn(true); } }} title={cameraOn ? "Stop Camera" : "Start Camera"} aria-label="Toggle camera">
                {cameraOn ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2m4-3h6l2 3h4a2 2 0 0 1 2 2v6" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                )}
              </button>
            </div>
          )}

          {/* Top-Right Viewport Actions */}
          {cameraOn && (
            <div
              className="tryon2__viewport-actions"
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                zIndex: 20,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              {/* Wishlist / Interested Button */}
              <button
                className={`tryon2__tool tryon2__viewport-wish ${selectedWishlisted ? 'tryon2__viewport-wish--active' : ''}`}
                onClick={handleWishlistToggle}
                disabled={!selectedProduct}
                title={selectedWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                aria-label={selectedWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                aria-pressed={selectedWishlisted}
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: selectedWishlisted ? '#fef2f2' : 'rgba(255, 255, 255, 0.92)',
                  border: selectedWishlisted ? '1px solid #ef4444' : 'none',
                  color: selectedWishlisted ? '#ef4444' : '#374151',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: selectedProduct ? 'pointer' : 'not-allowed',
                  opacity: selectedProduct ? 1 : 0.5,
                  transition: 'all 0.2s ease'
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill={selectedWishlisted ? '#ef4444' : 'none'}
                  stroke={selectedWishlisted ? '#ef4444' : 'currentColor'}
                  strokeWidth="2"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>

              {/* Fullscreen Toggle Button */}
              <button
                className="tryon2__tool tryon2__fullscreen-btn"
                onClick={toggleFullscreen}
                title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                aria-pressed={isFullscreen}
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 255, 255, 0.92)',
                  border: 'none',
                  color: '#1f2937',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {isFullscreen ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7" /></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3M10 21l7-7M14 3l-7 7" /></svg>
                )}
              </button>
            </div>
          )}

          {/* Carousel Pinned to Bottom */}
          <div className="tryon2__fs-carousel" style={{ position: 'absolute', left: '16px', right: '16px', bottom: '16px', zIndex: 20 }}>
            <div className="tryon2__fs-carousel-track">
              {loadingProducts ? (
                <div style={{ padding: '12px', color: '#6b7280', fontSize: '13px' }}>Loading frames...</div>
              ) : stripProducts.length === 0 ? (
                <div style={{ padding: '12px', color: '#6b7280', fontSize: '13px' }}>No frames available</div>
              ) : (
                stripProducts.map((product) => {
                  const isActive = selectedId === product.id;
                  const url = proxyFrameUrl(product) || '';
                  const fallback = resolveFrameUrl(product) || '';
                  return (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => selectFrame(product)}
                      title={product.product_name}
                      className={`tryon2__fs-card ${isActive ? 'tryon2__fs-card--selected' : ''}`}
                    >
                      {recommendMode && targetProduct?.id === product.id && (
                        <span className="tryon2__thumb-badge tryon2__fs-badge">Your pick</span>
                      )}
                      {!recommendMode && faceShape && isGoodFitForShape(product, faceShape) && (
                        <span className="tryon2__thumb-badge tryon2__thumb-badge--fit tryon2__fs-badge">Good fit</span>
                      )}
                      <span className="tryon2__fs-card-img">
                        <img
                          src={url}
                          alt={product.product_name}
                          loading="lazy"
                          crossOrigin="anonymous"
                          onError={handleThumbError(fallback)}
                        />
                      </span>
                      <span className="tryon2__fs-card-info">
                        <span className="tryon2__fs-card-name">{product.product_name}</span>
                        {pricingReady && <span className="tryon2__fs-card-price">₹{Math.round(product.selling_price).toLocaleString()}</span>}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          
          
        </div>
      </div>

      {/* Right panel only in compact modal */}
      {compact && (
        <div className="tryon2__panel">
          <div className="tryon2__panel-head">
            <div className="tryon2__brand">
              {selectedProduct?.brand && String(selectedProduct.brand).toLowerCase() !== 'nan'
                ? selectedProduct.brand : 'Virtual Try-On'}
            </div>
            <div className="tryon2__model">{selectedProduct?.product_name || 'Choose a frame'}</div>
          </div>

          <div className="tryon2__grid-wrap">
            {loadingProducts ? (
              <div className="tryon2__loading">Loading frames...</div>
            ) : (
              <div className="tryon2__grid">
                {stripProducts.map((product) => {
                  const url = proxyFrameUrl(product) || '';
                  const fallback = resolveFrameUrl(product) || '';
                  return (
                    <button
                      key={product.id}
                      onClick={() => selectFrame(product)}
                      title={product.product_name}
                      className={`tryon2__thumb ${selectedId === product.id ? 'tryon2__thumb--selected' : ''}`}
                    >
                      <span className="tryon2__thumb-img">
                        <img
                          src={url}
                          alt={product.product_name}
                          loading="lazy"
                          crossOrigin="anonymous"
                          onError={handleThumbError(fallback)}
                        />
                      </span>
                      {recommendMode && targetProduct?.id === product.id && (
                        <span className="tryon2__thumb-badge">Your pick</span>
                      )}
                      {!recommendMode && faceShape && isGoodFitForShape(product, faceShape) && (
                        <span className="tryon2__thumb-badge tryon2__thumb-badge--fit">Good fit</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selectedProduct && (
            <div className="tryon2__summary">
              <div className="tryon2__summary-info">
                {selectedProduct.brand && String(selectedProduct.brand).toLowerCase() !== 'nan' && (
                  <span className="tryon2__summary-brand">{selectedProduct.brand}</span>
                )}
                <span className="tryon2__summary-name">{selectedProduct.product_name}</span>
                {pricingReady && (
                  <span className="tryon2__summary-price">
                    ₹{Math.round(selectedProduct.selling_price).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          )}

          <button
            className="tryon2__cta"
            onClick={handleAddToCart}
            disabled={!canBuy || adding}
          >
            <span>{added ? '✓ Added' : adding ? 'Adding...' : pricingReady && !canBuy ? 'Out of Stock' : 'Add to Cart'}</span>
            {canBuy && <span className="tryon2__cta-price">₹{Math.round(selectedProduct.selling_price).toLocaleString()}</span>}
          </button>
        </div>
      )}


    
    </div >
  );
};

export default LiveTryonStage;